import type { RouteObject } from "react-router";
import { useNavigate } from "react-router";

import { Button, ButtonStyle, PageHeading } from "@prequel/shared-components";

import LoadDetails from "./LoadDetails";
import LoadPage from "./LoadPage";
import LoadRecordsTable from "./LoadRecordsTable";
import LoadsTable from "./LoadsTable";
import StreamAdmin from "./StreamAdmin";
import StreamForm from "./StreamForm";
import StreamOverview from "./StreamOverview";
import StreamPage, { StreamDetailsPage, useStreamContext } from "./StreamPage";
import StreamsTable from "./StreamsTable";

import Breadcrumbs from "@/components/Breadcrumbs";

const StreamsListLayout = () => {
  const navigate = useNavigate();

  return (
    <div className="h-full flex-col">
      <div className="max-w-7xl px-7 py-4 pb-16">
        <PageHeading title="Streams & Loads" breadcrumbs={<Breadcrumbs />}>
          <Button text="Add Stream" onClick={() => navigate("/import/streams/new")} type={ButtonStyle.SECONDARY} />
        </PageHeading>
        <StreamsTable />
      </div>
    </div>
  );
};

const StreamFormNewLayout = () => {
  const navigate = useNavigate();

  return (
    <div className="h-full flex-col">
      <div className="max-w-6xl px-7 py-4">
        <PageHeading title="Add Stream" onGoBack={() => navigate("/import/streams")} breadcrumbs={<Breadcrumbs />} />
        <StreamForm />
      </div>
    </div>
  );
};

const StreamFormEditLayout = () => {
  const navigate = useNavigate();
  const { stream, refreshStream } = useStreamContext();

  return (
    <div className="h-full flex-col">
      <div className="max-w-6xl px-7 py-4">
        <PageHeading
          title="Edit Stream"
          onGoBack={() => navigate(`/import/streams/${stream.id}`)}
          breadcrumbs={<Breadcrumbs dynamicLabel={stream.name} />}
        />
        <StreamForm
          streamId={stream.id}
          stream={stream}
          cancelPath={`/import/streams/${stream.id}`}
          onSaved={refreshStream}
        />
      </div>
    </div>
  );
};

export const streamsRouteChildren: RouteObject = {
  path: "streams",
  handle: {
    breadcrumb: () => ({ label: "Streams & Loads" }),
  },
  children: [
    {
      index: true,
      element: <StreamsListLayout />,
    },
    {
      path: "new",
      handle: {
        breadcrumb: () => ({ label: "Add Stream" }),
      },
      element: <StreamFormNewLayout />,
    },
    {
      path: ":streamID",
      handle: {
        breadcrumb: () => ({
          label: "Stream Details",
          dynamic: true,
        }),
      },
      element: <StreamPage />,
      children: [
        {
          path: "edit",
          handle: {
            breadcrumb: () => ({ label: "Edit Stream" }),
          },
          element: <StreamFormEditLayout />,
        },
        {
          element: <StreamDetailsPage />,
          children: [
            {
              index: true,
              element: <StreamOverview />,
            },
            {
              path: "loads",
              element: <LoadsTable />,
            },
            {
              path: "admin",
              element: <StreamAdmin />,
            },
          ],
        },
        {
          path: "loads/:loadID",
          handle: {
            breadcrumb: () => ({
              label: "Load Details",
              dynamic: true,
            }),
          },
          element: <LoadPage />,
          children: [
            {
              index: true,
              element: <LoadDetails />,
            },
            {
              path: "records",
              element: <LoadRecordsTable />,
            },
          ],
        },
      ],
    },
  ],
};
